import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { THEME_COLOR_NAMES, ThemeColorName } from '@examdojo/styles';
import type { JsxgraphTheme } from './jsxgraph.model';

/**
 * Service that provides theme values for JSXGraph widgets.
 * Reads CSS variables at runtime to support light/dark modes.
 *
 * This service only returns RAW VALUES (colors, typography, spacing).
 * It does NOT return JSXGraph API options - those are built in widget-base.
 */
@Injectable()
export class JsxgraphThemeService {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  private cachedTheme: JsxgraphTheme | null = null;

  // =============================================================================
  // THEME API
  // =============================================================================

  getTheme(): JsxgraphTheme {
    if (this.cachedTheme) {
      return this.cachedTheme;
    }

    this.cachedTheme = {
      colors: {
        axis: this.getCssVar('neutral', 300),
        grid: this.getCssVar('neutral', 100),
        background: this.getCssVar('neutral', 0),
        border: this.getCssVar('neutral', 200),
        textSecondary: this.getCssVar('secondary'),
        feature: THEME_COLOR_NAMES.map((name) => this.getCssVar(name, 500)),
        shading: THEME_COLOR_NAMES.reduce(
          (acc, name) => ({ ...acc, [name]: this.getCssVar(name, 50) }),
          {} as Record<ThemeColorName, string>,
        ),
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
        fontSize: {
          base: 16,
          small: 14,
          label: 12,
        },
        fontWeight: {
          normal: 400,
          medium: 500,
          bold: 600,
        },
      },
      spacing: {
        borderRadius: '16px',
        labelPadding: '2px 6px',
        labelOffset: [9, 19],
      },
      labelColors: {
        white: this.getCssVar('neutral', 0),
        bgLight: (color: ThemeColorName) => this.getCssVar(color, 100),
        textDark: (color: ThemeColorName) => this.getCssVar(color, 600),
        textDarker: (color: ThemeColorName) => this.getCssVar(color, 700),
      },
    };

    return this.cachedTheme;
  }

  invalidateCache(): void {
    this.cachedTheme = null;
  }

  // =============================================================================
  // COLOR HELPERS
  // =============================================================================

  getColor(color: ThemeColorName): string {
    return this.getCssVar(color, 500);
  }

  // =============================================================================
  // INTERNAL HELPERS
  // =============================================================================

  private getCssVar(name: string, shade?: number): string {
    const cssVar = shade !== undefined ? `--${name}-${shade}` : `--${name}`;
    const root = this.document.documentElement;
    return getComputedStyle(root).getPropertyValue(cssVar).trim();
  }
}
